import React, { useState, useEffect } from 'react';
import { getDisplayPhoneNumber } from '../../utils/numberMasking';
import axios from 'axios';
import { X, Users, AlertCircle, CheckCircle } from 'lucide-react';
import '../../styles/LeadManagement.css'; // Reuse existing styles or add new ones

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const LeadAssignmentModal = ({ isOpen, onClose, onAssignSuccess, totalUnassignedLeads }) => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('assign'); // 'assign' or 'unassign'
  
  // For assign tab
  const [unassignedLeads, setUnassignedLeads] = useState([]);
  const [selectedLeadsToAssign, setSelectedLeadsToAssign] = useState([]);
  const [selectedRoleForAssign, setSelectedRoleForAssign] = useState('');
  
  // For unassign tab
  const [assignedLeads, setAssignedLeads] = useState([]);
  const [selectedLeadsToUnassign, setSelectedLeadsToUnassign] = useState([]);

  useEffect(() => {
    if (isOpen) {
      setSelectedLeadsToAssign([]);
      setSelectedLeadsToUnassign([]);
      setSelectedRoleForAssign('');
      setError('');
      setSuccess('');
      fetchRoles();
      
      if (activeTab === 'assign') {
        fetchUnassignedLeads();
      } else {
        fetchAssignedLeads();
      }
    }
  }, [isOpen, activeTab]);

  // Helper function to get the appropriate token (RBAC token takes priority)
  const getAuthToken = () => {
    // Check for RBAC session first
    const rbacSessionStr = sessionStorage.getItem('rbac_session');
    if (rbacSessionStr) {
      try {
        const rbacSession = JSON.parse(rbacSessionStr);
        if (rbacSession.token) {
          console.log('Using RBAC token for API request');
          return rbacSession.token;
        }
      } catch (err) {
        console.error('Error parsing RBAC session:', err);
      }
    }
    // Fall back to main client token
    return localStorage.getItem('voicebot_access_token') || localStorage.getItem('admin_voicebot_access_token');
  };

  const fetchRoles = async () => {
    try {
      const token = getAuthToken();
      
      let response;
      try {
        response = await axios.get(`${API_BASE_URL}/rbac/roles`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (err) {
        response = await axios.get(`${API_BASE_URL}/admin-rbac/roles`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      if (response && response.data && response.data.success) {
        setRoles(response.data.data.filter(r => r.is_active));
      }
    } catch (err) {
      console.error('Error fetching RBAC roles:', err);
    }
  };

  const fetchUnassignedLeads = async () => {
    try {
      setLoading(true);
      setError('');
      const token = getAuthToken();
      
      const response = await axios.get(`${API_BASE_URL}/leads?limit=1000&assigned_to=unassigned`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setUnassignedLeads(response.data.leads);
      }
    } catch (err) {
      console.error('Error fetching unassigned leads:', err);
      setError('Failed to fetch unassigned leads.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignedLeads = async () => {
    try {
      setLoading(true);
      setError('');
      const token = getAuthToken();
      
      const response = await axios.get(`${API_BASE_URL}/leads?limit=1000`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const assigned = response.data.leads.filter(lead => lead.assigned_to !== null);
        setAssignedLeads(assigned);
      }
    } catch (err) {
      console.error('Error fetching assigned leads:', err);
      setError('Failed to fetch assigned leads.');
    } finally {
      setLoading(false);
    }
  };

  const toggleLeadSelectionForAssign = (leadId) => {
    setSelectedLeadsToAssign(prev => {
      if (prev.includes(leadId)) {
        return prev.filter(id => id !== leadId);
      }
      return [...prev, leadId];
    });
  };

  const toggleLeadSelectionForUnassign = (leadId) => {
    setSelectedLeadsToUnassign(prev => {
      if (prev.includes(leadId)) {
        return prev.filter(id => id !== leadId);
      }
      return [...prev, leadId];
    });
  };

  const selectAllUnassigned = () => {
    if (selectedLeadsToAssign.length === unassignedLeads.length) {
      setSelectedLeadsToAssign([]);
    } else {
      setSelectedLeadsToAssign(unassignedLeads.map(lead => lead.id));
    }
  };

  const selectAllAssigned = () => {
    if (selectedLeadsToUnassign.length === assignedLeads.length) {
      setSelectedLeadsToUnassign([]);
    } else {
      setSelectedLeadsToUnassign(assignedLeads.map(lead => lead.id));
    }
  };

  const handleAssign = async () => {
    if (selectedLeadsToAssign.length === 0) {
      setError('Please select at least one lead to assign.');
      return;
    }
    if (!selectedRoleForAssign) {
      setError('Please select a role to assign leads to.');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const token = getAuthToken();
      
      // Assign each selected lead to the role
      const promises = selectedLeadsToAssign.map(leadId =>
        axios.put(
          `${API_BASE_URL}/leads/${leadId}`,
          { assigned_to: selectedRoleForAssign },
          { headers: { Authorization: `Bearer ${token}` } }
        )
      );

      await Promise.all(promises);

      setSuccess(`Successfully assigned ${selectedLeadsToAssign.length} leads.`);
      setTimeout(() => {
        onAssignSuccess();
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Error assigning leads:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to assign leads.');
    } finally {
      setLoading(false);
    }
  };

  const handleUnassign = async () => {
    if (selectedLeadsToUnassign.length === 0) {
      setError('Please select at least one lead to unassign.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = getAuthToken();
      const response = await axios.post(
        `${API_BASE_URL}/leads/unassign-bulk`,
        { leadIds: selectedLeadsToUnassign },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSuccess(`Successfully unassigned ${response.data.unassignedCount} leads.`);
        setTimeout(() => {
          onAssignSuccess();
          onClose();
        }, 1500);
      }
    } catch (err) {
      console.error('Error unassigning leads:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to unassign leads.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh' }}>
        <div className="modal-header">
          <h3><Users size={20} className="inline-icon" /> Lead Assignment Manager</h3>
          <button onClick={onClose} className="close-btn"><X size={20} /></button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '2px solid #e2e8f0', padding: '0 20px' }}>
          <button
            onClick={() => setActiveTab('assign')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontWeight: activeTab === 'assign' ? '600' : '400',
              color: activeTab === 'assign' ? '#3b82f6' : '#64748b',
              borderBottom: activeTab === 'assign' ? '3px solid #3b82f6' : '3px solid transparent',
              marginBottom: '-2px'
            }}
          >
            📥 Assign Leads
          </button>
          <button
            onClick={() => setActiveTab('unassign')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontWeight: activeTab === 'unassign' ? '600' : '400',
              color: activeTab === 'unassign' ? '#3b82f6' : '#64748b',
              borderBottom: activeTab === 'unassign' ? '3px solid #3b82f6' : '3px solid transparent',
              marginBottom: '-2px'
            }}
          >
            📤 Unassign Leads
          </button>
        </div>

        <div className="modal-body" style={{ padding: '20px' }}>
          {error && (
            <div style={{ backgroundColor: '#fee2e2', color: '#b91c1c', padding: '12px', borderRadius: '6px', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <AlertCircle size={16} /> <span>{error}</span>
            </div>
          )}
          
          {success && (
            <div style={{ backgroundColor: '#dcfce7', color: '#15803d', padding: '12px', borderRadius: '6px', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CheckCircle size={16} /> <span>{success}</span>
            </div>
          )}

          {activeTab === 'assign' ? (
            <div>
              {/* Role Selection */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>
                  Select Role to Assign Leads To:
                </label>
                <select
                  value={selectedRoleForAssign}
                  onChange={(e) => setSelectedRoleForAssign(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '14px'
                  }}
                >
                  <option value="">-- Select a Role --</option>
                  {roles.map(role => (
                    <option key={role.id} value={role.id}>
                      {role.role_name} (@{role.username})
                    </option>
                  ))}
                </select>
              </div>

              {/* Lead Selection */}
              <div className="lead-list-header" style={{ marginBottom: '12px' }}>
                <div className="lead-list-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <h4 style={{ margin: 0, color: '#374151' }}>
                    Select Leads ({selectedLeadsToAssign.length} selected)
                  </h4>
                </div>
                <div className="lead-list-controls" style={{ marginTop: '8px', display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    onClick={selectAllUnassigned}
                    className="select-all-assign"
                    style={{
                      padding: '6px 12px',
                      background: '#f3f4f6',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '13px'
                    }}
                  >
                    {selectedLeadsToAssign.length === unassignedLeads.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
              </div>

              <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px' }}>
                {loading ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading leads...</div>
                ) : unassignedLeads.length === 0 ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                    No unassigned leads available
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ position: 'sticky', top: 0, background: '#f9fafb' }}>
                      <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ padding: '12px', textAlign: 'left', width: '40px' }}></th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Name</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Mobile</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Referred By</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Source</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unassignedLeads.map(lead => (
                        <tr
                          key={lead.id}
                          onClick={() => toggleLeadSelectionForAssign(lead.id)}
                          style={{
                            borderBottom: '1px solid #e5e7eb',
                            cursor: 'pointer',
                            background: selectedLeadsToAssign.includes(lead.id) ? '#eff6ff' : 'white'
                          }}
                        >
                          <td style={{ padding: '12px' }}>
                            <input
                              type="checkbox"
                              className="lead-assignment-modal-checkbox"
                              checked={selectedLeadsToAssign.includes(lead.id)}
                              onChange={() => {}}
                              aria-label={`Select lead ${lead.customer_name || lead.id}`}
                              style={{ cursor: 'pointer' }}
                            />
                          </td>
                          <td style={{ padding: '12px', color: '#111827' }}>{lead.customer_name || 'N/A'}</td>
                          <td style={{ padding: '12px', color: '#111827' }}>{getDisplayPhoneNumber(lead.mobile_number)}</td>
                          <td style={{ padding: '12px', color: '#111827' }}>{lead.referred_by || '-'}</td>
                          <td style={{ padding: '12px' }}>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '12px',
                              background: '#f3f4f6',
                              fontSize: '12px',
                              color: '#374151'
                            }}>
                              {lead.source}
                            </span>
                          </td>
                          <td style={{ padding: '12px' }}>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '12px',
                              background: '#dbeafe',
                              fontSize: '12px',
                              color: '#1e40af'
                            }}>
                              {lead.lead_status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          ) : (
            <div>
              {/* Unassign Tab */}
              <div className="lead-list-header" style={{ marginBottom: '12px' }}>
                <div className="lead-list-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <h4 style={{ margin: 0, color: '#374151' }}>
                    Select Leads to Unassign ({selectedLeadsToUnassign.length} selected)
                  </h4>
                </div>
                <div className="lead-list-controls" style={{ marginTop: '8px', display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    onClick={selectAllAssigned}
                    className="select-all-unassign"
                    style={{
                      padding: '6px 12px',
                      background: '#f3f4f6',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '13px'
                    }}
                  >
                    {selectedLeadsToUnassign.length === assignedLeads.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
              </div>

              <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px' }}>
                {loading ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading leads...</div>
                ) : assignedLeads.length === 0 ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                    No assigned leads found
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ position: 'sticky', top: 0, background: '#f9fafb' }}>
                      <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ padding: '12px', textAlign: 'left', width: '40px' }}></th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Name</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Mobile</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Referred By</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Assigned To</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assignedLeads.map(lead => (
                        <tr
                          key={lead.id}
                          onClick={() => toggleLeadSelectionForUnassign(lead.id)}
                          style={{
                            borderBottom: '1px solid #e5e7eb',
                            cursor: 'pointer',
                            background: selectedLeadsToUnassign.includes(lead.id) ? '#fef2f2' : 'white'
                          }}
                        >
                          <td style={{ padding: '12px' }}>
                            <input
                              type="checkbox"
                              className="lead-assignment-modal-checkbox"
                              checked={selectedLeadsToUnassign.includes(lead.id)}
                              onChange={() => {}}
                              aria-label={`Select lead to unassign ${lead.customer_name || lead.id}`}
                              style={{ cursor: 'pointer' }}
                            />
                          </td>
                          <td style={{ padding: '12px', color: '#111827' }}>{lead.customer_name || 'N/A'}</td>
                          <td style={{ padding: '12px', color: '#111827' }}>{getDisplayPhoneNumber(lead.mobile_number)}</td>
                          <td style={{ padding: '12px', color: '#111827' }}>{lead.referred_by || '-'}</td>
                          <td style={{ padding: '12px' }}>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '12px',
                              background: '#e0e7ff',
                              fontSize: '12px',
                              color: '#3730a3'
                            }}>
                              {lead.assigned_to_role_name || lead.assigned_to_username || 'Unknown'}
                            </span>
                          </td>
                          <td style={{ padding: '12px' }}>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '12px',
                              background: '#dbeafe',
                              fontSize: '12px',
                              color: '#1e40af'
                            }}>
                              {lead.lead_status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="modal-actions" style={{ padding: '15px 20px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button 
            className="btn btn-secondary" 
            onClick={onClose}
            disabled={loading}
            style={{ padding: '10px 20px' }}
          >
            Cancel
          </button>
          {activeTab === 'assign' ? (
            <button 
              className="btn btn-primary" 
              onClick={handleAssign}
              disabled={loading || selectedLeadsToAssign.length === 0 || !selectedRoleForAssign}
              style={{ padding: '10px 20px', display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              {loading ? 'Assigning...' : `Assign ${selectedLeadsToAssign.length} Leads`}
            </button>
          ) : (
            <button 
              className="btn"
              onClick={handleUnassign}
              disabled={loading || selectedLeadsToUnassign.length === 0}
              style={{ 
                padding: '10px 20px',
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                background: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: loading || selectedLeadsToUnassign.length === 0 ? 'not-allowed' : 'pointer',
                opacity: loading || selectedLeadsToUnassign.length === 0 ? 0.5 : 1
              }}
            >
              {loading ? 'Unassigning...' : `Unassign ${selectedLeadsToUnassign.length} Leads`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadAssignmentModal;
